import { Client } from 'src/admin/users/types';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useClients } from 'src/clients/hooks/useClients';
import { Family, useFamily } from 'src/clients/hooks/useFamily';

export type FamilyMember = {
  clientId: string;
  client: Client;
  isMain: boolean;
};

// Single source of truth for resolving a family's members. Both the family
// screen and the individual screen build their member list from this so the two
// can't drift: role-model families keep every member in `family.members` with no
// secondary rows, so those must be unioned in or non-primary members get hidden
// when the family is read through the star shape.
export function buildFamilyMembers(
  family: Family | null,
  clientById: Record<string, Client>,
): FamilyMember[] {
  if (!family) return [];

  const mainClientId = family.mainFamilyMember.clientId;
  const secondaryIds = family.secondaryFamilyMembers.map(m => m.clientId);
  const mainClient = clientById[mainClientId];

  const seen = new Set([mainClientId, ...secondaryIds]);
  const roleModelIds = family.members.map(m => m.clientId).filter(id => !seen.has(id));

  const secondaryMembers: FamilyMember[] = [...secondaryIds, ...roleModelIds]
    .map(id => {
      const c = clientById[id];
      if (!c) return null;
      return { clientId: id, client: c, isMain: false };
    })
    .filter((m): m is FamilyMember => m !== null)
    .sort((a, b) => {
      const aName = `${a.client.firstName} ${a.client.lastName}`.toLowerCase();
      const bName = `${b.client.firstName} ${b.client.lastName}`.toLowerCase();
      return aName.localeCompare(bName);
    });

  return [
    ...(mainClient ? [{ clientId: mainClientId, client: mainClient, isMain: true }] : []),
    ...secondaryMembers,
  ];
}

export function useFamilyMembers({ enabled = true }: { enabled?: boolean } = {}): {
  members: FamilyMember[];
  hasOtherMembers: boolean;
  isLoading: boolean;
} {
  const { client, clientId } = useCurrentClient();
  const familyId = client?.familyId;

  const { family, isLoading: isFamilyLoading } = useFamily(enabled ? familyId : undefined);
  const { clients, isLoading: isClientsLoading } = useClients(
    enabled && familyId ? { familyId } : {},
    { enabled },
  );

  const isLoading = isFamilyLoading || isClientsLoading;

  if (!familyId || !family) {
    return { members: [], hasOtherMembers: false, isLoading };
  }

  const clientById = Object.fromEntries(clients.map(c => [c.id, c]));

  const members = buildFamilyMembers(family, clientById);

  const hasOtherMembers = members.some(m => m.clientId !== clientId);

  return { members, hasOtherMembers, isLoading };
}
